import { XIcon } from "lucide-react";
import { Dialog as SheetPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export const Sheet = SheetPrimitive.Root;
export const SheetClose = SheetPrimitive.Close;

/** Side panel for entity forms; full-height bottom sheet on phones. */
export function SheetContent({
  className,
  children,
  ...props
}: ComponentProps<typeof SheetPrimitive.Content>) {
  const { t } = useTranslation();
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
      <SheetPrimitive.Content
        className={cn(
          "fixed z-50 flex flex-col bg-surface shadow-lg outline-none",
          "inset-x-0 top-[4dvh] bottom-0 rounded-t-2xl border-t border-border pb-[env(safe-area-inset-bottom)] data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom",
          "sm:inset-y-2 sm:right-2 sm:left-auto sm:w-[min(560px,calc(100vw-1rem))] sm:rounded-2xl sm:border sm:pb-0 sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right",
          "duration-200",
          className,
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none"
          aria-label={t("common.close")}
        >
          <XIcon className="size-4" />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

export function SheetHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border px-5 pt-5 pr-12 pb-4">
      <SheetPrimitive.Title className="text-base font-semibold tracking-tight">
        {title}
      </SheetPrimitive.Title>
      {description ? (
        <SheetPrimitive.Description className="text-sm text-muted-foreground">
          {description}
        </SheetPrimitive.Description>
      ) : (
        <SheetPrimitive.Description className="sr-only">{title}</SheetPrimitive.Description>
      )}
    </div>
  );
}

export function SheetBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("min-h-0 flex-1 scrollbar-thin overflow-y-auto px-5 py-5", className)}
      {...props}
    />
  );
}

export function SheetFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t border-border bg-muted/40 px-5 py-3 sm:rounded-b-2xl",
        className,
      )}
      {...props}
    />
  );
}
